import React, { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { Box, Typography, Collapse, IconButton, List, ListItem, ListItemText, Divider, Chip, CircularProgress, Dialog, DialogContent, DialogActions, Button, Zoom, Tooltip, useMediaQuery } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import TimerIcon from '@mui/icons-material/Timer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useLanguage } from '../contexts/LanguageContext';

mermaid.initialize({
  startOnLoad: true,
  theme: 'base',
  securityLevel: 'loose',
  themeVariables: {
    fontSize: '16px',
    fontFamily: 'arial'
  }
});

// Status color mapping
const statusColors = {
  completed0: '#e6f7ef',
  completed1: '#c3ebd7',
  completed2: '#9fdfbf',
  completed3: '#7bd3a7',
  active: '#fff3cd',
  pending: '#e2eafc',
  unknown: '#f5f5f5'
};

// Status label mapping
const getStatusLabels = (tr) => ({
  completed0: tr('已完成 (0-30%)', 'Completed (0-30%)'),
  completed1: tr('已完成 (30-60%)', 'Completed (30-60%)'),
  completed2: tr('已完成 (60-90%)', 'Completed (60-90%)'),
  completed3: tr('已完成 (90-100%)', 'Completed (90-100%)'),
  active: tr('進行中', 'In Progress'),
  pending: tr('待處理', 'Pending'),
  unknown: tr('未知', 'Unknown')
});

const Mermaid = ({ chart, nodes, isLoading, scenarioData, checkpointStatus }) => {
  const { tr } = useLanguage();
  const statusLabels = getStatusLabels(tr);
  const elementRef = useRef(null);
  const fullscreenRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [showNodeList, setShowNodeList] = useState(true);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [svgContent, setSvgContent] = useState('');
  const [expandedCheckpoints, setExpandedCheckpoints] = useState({});
  
  // Add a check for mobile view using useMediaQuery
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));

  // Update the initial expansion state based on viewport size
  useEffect(() => {
    setIsExpanded(!isMobile); // Collapse on mobile, expand on desktop
    setShowNodeList(!isMobile); // Also collapse node list on mobile
  }, [isMobile]);

  useEffect(() => {
    const renderChart = async () => {
      if (elementRef.current) {
        elementRef.current.innerHTML = '';
        
        try {
          // Check if chart is valid
          if (!chart || typeof chart !== 'string' || chart.trim() === '') {
            elementRef.current.innerHTML = `<div style="padding: 20px; color: #666;">${tr('等待流程圖數據...', 'Waiting for flowchart data...')}</div>`;
            return;
          }
          
          // Generate a valid ID without dots or special characters
          const id = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
          const { svg } = await mermaid.render(id, chart);
          elementRef.current.innerHTML = svg;
          setSvgContent(svg);
          
          // Add click event to the SVG for fullscreen
          const svgElement = elementRef.current.querySelector('svg');
          if (svgElement) {
            svgElement.style.cursor = 'pointer';
            svgElement.addEventListener('click', handleOpenFullscreen);
          }
          
        } catch (error) {
          console.error('Error rendering mermaid chart:', error);
          elementRef.current.innerHTML = `<div style="padding: 20px; color: #f44336;">${tr('無法渲染流程圖。請稍後再試。', 'Unable to render the flowchart. Please try again later.')}</div>`;
        }
      }
    };

    if (isExpanded) {
      renderChart();
    }
  }, [chart, isExpanded, tr]);

  // Render the fullscreen chart when dialog opens
  useEffect(() => {
    const renderFullscreenChart = async () => {
      if (fullscreenOpen && fullscreenRef.current && chart) {
        fullscreenRef.current.innerHTML = '';
        
        try {
          // Generate a valid ID without dots or special characters
          const id = `mermaid-fullscreen-${Math.random().toString(36).substr(2, 9)}`;
          const { svg } = await mermaid.render(id, chart);
          
          // Create a container div with proper styling for scaling
          const containerDiv = document.createElement('div');
          containerDiv.style.transform = 'scale(1.5)';
          containerDiv.style.transformOrigin = 'top left';
          containerDiv.style.width = '33%'; // 1/3 of the width to account for 3x scale
          containerDiv.style.height = 'auto';
          containerDiv.style.margin = '0 auto';
          containerDiv.innerHTML = svg;
          
          // Clear and append
          fullscreenRef.current.innerHTML = '';
          fullscreenRef.current.appendChild(containerDiv);
          
          // Get the SVG element and ensure it's properly sized
          const svgElement = containerDiv.querySelector('svg');
          if (svgElement) {
            svgElement.style.width = '100%';
            svgElement.style.height = 'auto';
            svgElement.style.maxWidth = 'none';
          }
        } catch (error) {
          console.error('Error rendering fullscreen mermaid chart:', error);
          fullscreenRef.current.innerHTML = 'Error rendering flowchart';
        }
      }
    };

    if (fullscreenOpen) {
      // Small delay to ensure the dialog is fully open before rendering
      setTimeout(() => {
        renderFullscreenChart();
      }, 100);
    }
  }, [fullscreenOpen, chart]);

  // Function to handle opening fullscreen dialog
  const handleOpenFullscreen = () => {
    setFullscreenOpen(true);
  };

  // Function to handle closing fullscreen dialog
  const handleCloseFullscreen = () => {
    setFullscreenOpen(false);
  };

  // Function to get all nodes flattened with their status
  const getAllNodes = () => {
    if (!nodes || !Array.isArray(nodes) || nodes.length === 0) {
      return [{ id: 'empty', text: tr('等待節點數據...', 'Waiting for node data...'), type: 'subgraph', status: 'header' }];
    }
    
    const allNodes = [];
    nodes.forEach((subgraph, subgraphIndex) => {
      // Add subgraph as a header with letter label (A, B, C, etc.)
      const subgraphLabel = String.fromCharCode(65 + subgraphIndex); // 65 is ASCII for 'A'
      
      // Find duration for this stage from scenarioData if available
      let duration = null;
      if (scenarioData && scenarioData.stage) {
        const stageData = scenarioData.stage.find(s => s.name === subgraph.text);
        if (stageData && stageData.duration) {
          duration = stageData.duration;
        }
      }
      
      // Use duration from node if available, otherwise from scenarioData
      const finalDuration = subgraph.duration || duration;
      let durationText = finalDuration ? tr(` (${finalDuration} 分鐘)`, ` (${finalDuration} min)`) : '';
      
      allNodes.push({
        id: subgraph.id,
        text: `${subgraphLabel}. ${subgraph.text}`,
        type: 'subgraph',
        status: 'header',
        duration: finalDuration
      });
      
      // Add all nodes in the subgraph with number labels
      if (subgraph.nodes) {
        subgraph.nodes.forEach((node, nodeIndex) => {
          // Create a copy of the node with modified text
          const nodeWithLabel = {
            ...node,
            text: `${nodeIndex + 1}. ${node.text}`
          };
          allNodes.push(nodeWithLabel);
        });
      }
    });
    
    return allNodes;
  };

  return (
    <Box>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            cursor: 'pointer',
          }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <IconButton size="small">
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Typography variant="h6">
            {tr('流程圖', 'Flowchart')}
          </Typography>
        </Box>
        
        {/* Fullscreen button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={tr('全螢幕檢視', 'Full-screen view')}>
            <IconButton 
              size="small" 
              onClick={handleOpenFullscreen}
              disabled={isLoading}
            >
              <FullscreenIcon />
            </IconButton>
          </Tooltip>
          
          {/* Loading indicator */}
          {isLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="caption" color="text.secondary">
                {tr('更新中...', 'Updating...')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      
      <Collapse in={isExpanded}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Node List Section - Now at the top */}
          <Box>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                py: 1
              }}
            >
              <Box 
                sx={{ display: 'flex', alignItems: 'center' }}
                onClick={() => setShowNodeList(!showNodeList)}
              >
                <IconButton size="small">
                  {showNodeList ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <Typography variant="subtitle1">
                  {tr('節點狀態列表', 'Node Status')}
                </Typography>
              </Box>
              
              {/* Loading indicator for node list */}
              {isLoading && (
                <CircularProgress size={16} sx={{ mr: 1 }} />
              )}
            </Box>
            
            <Collapse in={showNodeList}>
              <Box sx={{ position: 'relative' }}>
                <List sx={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #eee', borderRadius: '4px' }}>
                  {getAllNodes().map((node, index) => (
                    <React.Fragment key={`${node.id}-${index}`}>
                      {node.status === 'header' ? (
                        <ListItem sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }}>
                          <ListItemText 
                            primary={node.text} 
                            primaryTypographyProps={{ fontWeight: 'bold' }}
                          />
                          {node.duration && (
                            <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                              <TimerIcon fontSize="small" sx={{ mr: 0.5, color: 'primary.main' }} />
                              <Typography variant="body2" color="primary.main" fontWeight="medium">
                                {tr(`${node.duration} 分鐘`, `${node.duration} min`)}
                              </Typography>
                            </Box>
                          )}
                        </ListItem>
                      ) : (
                        <ListItem sx={{ pl: 4 }}>
                          <ListItemText 
                            primary={node.text} 
                            secondary={
                              node.checkpoints && node.checkpoints.length > 0 ? (
                                <Box sx={{ mt: 1 }}>
                                  <Box 
                                    sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center', 
                                      cursor: 'pointer',
                                      mb: 0.5
                                    }}
                                    onClick={() => {
                                      setExpandedCheckpoints(prev => ({
                                        ...prev,
                                        [node.id]: !prev[node.id]
                                      }));
                                    }}
                                  >
                                    <IconButton size="small" sx={{ p: 0, mr: 0.5 }}>
                                      {expandedCheckpoints[node.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                    </IconButton>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                                      {tr(`查核點 (${node.checkpoints.length})`, `Checkpoints (${node.checkpoints.length})`)}
                                    </Typography>
                                  </Box>
                                  <Collapse in={expandedCheckpoints[node.id]}>
                                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                      {node.checkpoints.map((checkpoint, idx) => {
                                        // Check if this checkpoint is completed
                                        const nodeCheckpointStatus = checkpointStatus && checkpointStatus[node.id];
                                        const isCompleted = nodeCheckpointStatus && 
                                          nodeCheckpointStatus.completed_checkpoints && 
                                          nodeCheckpointStatus.completed_checkpoints.includes(idx);
                                        
                                        return (
                                          <Box 
                                            key={idx}
                                            sx={{ 
                                              display: 'flex', 
                                              alignItems: 'flex-start',
                                              mb: 0.5,
                                            }}
                                          >
                                            {nodeCheckpointStatus ? (
                                              isCompleted ? (
                                                <CheckCircleIcon 
                                                  fontSize="small" 
                                                  sx={{ 
                                                    color: 'success.main', 
                                                    mr: 0.5,
                                                    fontSize: '0.875rem'
                                                  }} 
                                                />
                                              ) : (
                                                <CancelIcon 
                                                  fontSize="small" 
                                                  sx={{ 
                                                    color: 'text.disabled', 
                                                    mr: 0.5,
                                                    fontSize: '0.875rem'
                                                  }} 
                                                />
                                              )
                                            ) : (
                                              <RadioButtonUncheckedIcon 
                                                fontSize="small" 
                                                sx={{ 
                                                  color: 'text.disabled', 
                                                  mr: 0.5,
                                                  fontSize: '0.875rem'
                                                }} 
                                              />
                                            )}
                                            <Typography 
                                              component="li" 
                                              variant="caption" 
                                              sx={{ 
                                                color: isCompleted ? 'text.primary' : 'text.secondary',
                                                fontSize: '0.75rem',
                                                fontWeight: isCompleted ? 'medium' : 'normal',
                                                textDecoration: isCompleted ? 'none' : 'none',
                                              }}
                                            >
                                              {checkpoint}
                                            </Typography>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Collapse>
                                </Box>
                              ) : null
                            }
                          />
                          <Chip 
                            label={statusLabels[node.status || 'unknown']} 
                            size="small"
                            sx={{ 
                              bgcolor: statusColors[node.status || 'unknown'],
                              border: `1px solid ${node.status === 'active' ? '#ffc107' : '#ccc'}`,
                              fontWeight: node.status === 'active' ? 'bold' : 'normal'
                            }}
                          />
                        </ListItem>
                      )}
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
                
                {/* Overlay for loading state */}
                {isLoading && (
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    borderRadius: '4px'
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: 'center',
                      gap: 1
                    }}>
                      <CircularProgress size={30} />
                      <Typography variant="caption">{tr('更新節點狀態...', 'Updating node status...')}</Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Box>
          
          {/* Flowchart Section - Now at the bottom */}
          <Box sx={{ position: 'relative' }}>
            <Box 
              ref={elementRef} 
              sx={{
                overflow: 'auto',
                maxWidth: '100%',
                mt: 2,
                border: '1px solid #eee',
                borderRadius: '4px',
                p: 1,
                minHeight: '200px'
              }}
            />
            
            {/* Overlay for loading state */}
            {isLoading && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                borderRadius: '4px'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  gap: 1
                }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2">{tr('生成流程圖中...', 'Generating flowchart...')}</Typography>
                  <RefreshIcon sx={{ animation: 'spin 2s linear infinite', mt: 1 }} />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Collapse>
      
      {/* Fullscreen Dialog */}
      <Dialog
        open={fullscreenOpen}
        onClose={handleCloseFullscreen}
        fullScreen
        TransitionComponent={Zoom}
      >
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Typography variant="h6">{tr('流程圖 (3x 放大)', 'Flowchart (3x zoom)')}</Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseFullscreen}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogActions>
        <DialogContent sx={{ 
          p: 4, 
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          bgcolor: '#f8f9fa'
        }}>
          <Box 
            ref={fullscreenRef} 
            sx={{
              overflow: 'auto',
              width: '100%',
              minHeight: '200px',
              p: 2,
              bgcolor: 'white',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              display: 'flex',
              justifyContent: 'center'
            }}
          />
        </DialogContent>
      </Dialog>
      
      {/* Add keyframes for the spinning animation */}
      <style jsx global>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default Mermaid; 